import { EmbeddedMessage } from 'constants/messageMediums';
import { selectIsInAppRecallUIFeatureFlagOn } from 'core/userContext';
import { StyledDivider } from 'modules/productAnalytics/Styled';
import {
  formatDeviceData,
  getChartActions,
  getChartExportingOptions,
} from 'modules/productAnalytics/utils';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { Skeleton } from 'ui/skeleton';
import { useLocationAndDevice } from '../hooks';
import { BounceMetricCard } from './BounceMetricCard';
import { CampaignAnalyticsPieChart } from './CampaignAnalyticsPieChart';
import { DeliveriesProgressBarChart } from './DeliveriesProgressBarChart';
import { LayoutTemplate } from './LayoutTemplate';
import { MetricBreakdown } from './MetricBreakdown';
import { MetricBreakdownCard } from './MetricBreakdownCard';
import { SendSkipCard } from './SendSkipCard';

import {
  DELIVERY_GRAPH_NICENAME,
  DELIVERY_LAYOUT,
  DELIVERY_SECTION_TO_GRAPH_METRIC,
} from '../constants';
import {
  selectCampaignAnalyticsStatsForTimerange,
  selectCampaignMetrics,
  selectIsLoading,
  selectMessageMedium,
} from '../selectors';
import { parseMetricsForBreakdown, parseMetricsForDeliveriesGraph } from '../utils';
import {
  DeviceTableWrapper,
  LeftCol,
  MetricBreakdownContainer,
  MetricBreakdownHeaderContainer,
  MetricList,
  MetricRow,
  RightCol,
  Row,
  SkeletonWrapper,
} from './Styled';

export const CampaignAnalyticsDelivery = () => {
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const chartRef = useRef();

  const isLoading = useSelector(selectIsLoading);
  const messageMedium = useSelector(selectMessageMedium);
  const campaignMetrics = useSelector(selectCampaignMetrics);

  const inAppRecallFF = useSelector(selectIsInAppRecallUIFeatureFlagOn);

  const showDevicesForFPO = useLocationAndDevice();

  const sendSkipBuckets = campaignMetrics?.['Send Skip Reasons Breakdown']?.value?.buckets || {};
  const getDeliveryGraphData = (graphMetric) =>
    graphMetric &&
    parseMetricsForDeliveriesGraph(
      DELIVERY_GRAPH_NICENAME(inAppRecallFF)[messageMedium].filter(
        (graphData) => graphData.metric === graphMetric,
      ) || [],
      statsForTimeRange?.results,
    );
  const getMetricBreakdown = (section) => {
    const graphMetric = DELIVERY_SECTION_TO_GRAPH_METRIC[messageMedium]?.[section.header];
    const deliveryGraphData = getDeliveryGraphData(graphMetric);

    if (section.header.includes('Bounce')) {
      return (
        <BounceMetricCard
          metricValues={section.values}
          results={statsForTimeRange?.results ?? {}}
          isLoading={isLoading}
          key="Bounces"
          headerChart={
            deliveryGraphData && <DeliveriesProgressBarChart data={deliveryGraphData?.[0]} />
          }
        />
      );
    }
    const sectionOverview = parseMetricsForBreakdown(
      section.values || [],
      statsForTimeRange?.results,
    );
    const [sectionOverviewHeader, ...sectionOverviewList] = sectionOverview;

    return (
      <MetricBreakdownCard key={section.header} title={section.header}>
        {isLoading ? (
          <SkeletonWrapper height={256} padding={24}>
            <Skeleton />
          </SkeletonWrapper>
        ) : (
          <MetricBreakdown
            metricGrid={sectionOverviewList}
            headerMetric={sectionOverviewHeader?.[0]}
            headerChart={
              deliveryGraphData && <DeliveriesProgressBarChart data={deliveryGraphData?.[0]} />
            }
          />
        )}
      </MetricBreakdownCard>
    );
  };

  /**
   * getDeviceCard
   * filtered - Boolean that determines if the Device data card uses the filtered proxied opens
   */
  const getDeviceCard = (filtered = false) => {
    const deviceDataMetric = filtered ? 'Device Data (filtered)' : 'Device Data';
    const deviceDataTitle = filtered
      ? 'Devices by total email opens (filtered)'
      : 'Devices by total email opens';
    const deviceData = statsForTimeRange?.results?.[deviceDataMetric]?.value;
    const chartId = 'devicePieChart';
    const chartActions = getChartActions(chartId, chartRef);
    if (!deviceData || (deviceData && Object.keys(deviceData).length === 0)) {
      return <></>;
    }
    const formattedData = formatDeviceData(deviceData);

    return (
      <MetricBreakdownCard
        title={deviceDataTitle}
        menuOptions={getChartExportingOptions(chartId)}
        handleSelectionChange={(key) => chartActions[key]()}
      >
        {isLoading ? (
          <SkeletonWrapper height={256} padding={24}>
            <Skeleton />
          </SkeletonWrapper>
        ) : (
          <MetricBreakdownContainer>
            <MetricBreakdownHeaderContainer>
              <CampaignAnalyticsPieChart data={formattedData} ref={chartRef} />
            </MetricBreakdownHeaderContainer>
            <StyledDivider />
            <DeviceTableWrapper>
              {Object.keys(deviceData)
                ?.sort((deviceA, deviceB) => deviceData[deviceB] - deviceData[deviceA])
                ?.map((key, idx) => (
                  <MetricList key={`metric-row-${idx}`}>
                    <MetricRow>
                      <span>{key === '-' ? 'Unknown' : key}</span>
                      <span>
                        {typeof deviceData[key] === 'number'
                          ? deviceData[key].toLocaleString()
                          : deviceData[key]}
                      </span>
                    </MetricRow>
                  </MetricList>
                ))}
            </DeviceTableWrapper>
          </MetricBreakdownContainer>
        )}
      </MetricBreakdownCard>
    );
  };

  const DeliveryLeftColData = DELIVERY_LAYOUT(inAppRecallFF)[messageMedium]?.left || [];
  const DeliveryRightColData = DELIVERY_LAYOUT(inAppRecallFF)[messageMedium]?.right || [];

  const SendSkipProgressChartData = getDeliveryGraphData(
    DELIVERY_SECTION_TO_GRAPH_METRIC[messageMedium]?.['Send skips'],
  );
  return (
    <LayoutTemplate pageName="Delivery" canShowFPOBanner>
      <Row>
        {messageMedium === 'WebPush' ? (
          <LeftCol>
            <SendSkipCard
              medium={messageMedium}
              results={statsForTimeRange?.results ?? {}}
              isLoading={isLoading}
              sendSkipBuckets={sendSkipBuckets}
              headerChart={
                SendSkipProgressChartData && (
                  <DeliveriesProgressBarChart data={SendSkipProgressChartData?.[0]} />
                )
              }
            />
          </LeftCol>
        ) : (
          <>
            <LeftCol>
              {DeliveryLeftColData.map((section) => getMetricBreakdown(section))}
              {messageMedium === 'Email' &&
                (showDevicesForFPO ? getDeviceCard(true) : getDeviceCard(false))}
            </LeftCol>
            <RightCol>
              {DeliveryRightColData.map((section) => getMetricBreakdown(section))}
              {messageMedium !== EmbeddedMessage && (
                <SendSkipCard
                  medium={messageMedium}
                  results={statsForTimeRange?.results ?? {}}
                  isLoading={isLoading}
                  sendSkipBuckets={sendSkipBuckets}
                  headerChart={
                    SendSkipProgressChartData && (
                      <DeliveriesProgressBarChart data={SendSkipProgressChartData?.[0]} />
                    )
                  }
                />
              )}
              {messageMedium === 'Email' && showDevicesForFPO && getDeviceCard(false)}
            </RightCol>
          </>
        )}
      </Row>
    </LayoutTemplate>
  );
};

export default withRouter(CampaignAnalyticsDelivery);
